import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, Recycle, Clock, DollarSign, CheckCircle, ArrowRight, HardHat, Building2, ChevronDown, AlertTriangle, Scale, MessageSquare, Sparkles, Mail, Lock } from "lucide-react";
import { toast } from "sonner";
import ContactSupportModal from "../components/ContactSupportModal";
import BrandWordmark from "../components/BrandWordmark";
import BlueprintBackdrop from "../components/BlueprintBackdrop";
import SEOHead from "../components/SEOHead";
import CountyNavLinks from "../components/CountyNavLinks";
import CalGreenWordmark from "../components/CalGreenWordmark";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LandingPage = () => {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const [pricing, setPricing] = useState({
    cwmp_price: 59,
    final_report_price: 49,
    bundle_price: 89,
    subscription_price: 99
  });
  // Inline login form state (right rail of the hero)
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);

  const handleInlineLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const r = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: loginEmail, password: loginPassword }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Login failed");
      localStorage.setItem("token", data.access_token);
      toast.success("Welcome back!");
      navigate("/dashboard", { state: { user: data.user } });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoginLoading(false);
    }
  };

  // Fetch pricing from API
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const response = await fetch(`${API}/pricing`);
        if (response.ok) {
          const data = await response.json();
          setPricing(data);
        }
      } catch (error) {
        console.log('Using default pricing');
      }
    };
    fetchPricing();
  }, []);

  const features = [
    {
      icon: <FileText className="w-8 h-8" />,
      title: "CALGreen Compliant",
      description: "Generate professional waste management plans that meet California's strict building code requirements."
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: "Instant Generation",
      description: "Create your CWMP in minutes, not days. Skip the consultant back-and-forth."
    },
    {
      icon: <DollarSign className="w-8 h-8" />,
      title: "Save Thousands",
      description: "Professional plans at a fraction of traditional consultant costs."
    },
    {
      icon: <Recycle className="w-8 h-8" />,
      title: "65% Diversion",
      description: "Auto-calculate material estimates and diversion rates to meet CALGreen targets."
    }
  ];

  // Calculate savings for bundle
  const bundleSavings = (pricing.cwmp_price + pricing.final_report_price) - pricing.bundle_price;

  const plans = [
    {
      name: "CWMP Report",
      price: `$${pricing.cwmp_price}`,
      description: "Pre-construction waste management plan",
      features: [
        "CALGreen compliant document",
        "Detailed waste calculations",
        "Material breakdown tables",
        "Jurisdiction-specific notes",
        "Facility & hauler suggestions",
        "AI Compliance Memo (1 included)",
        "Professional PDF export"
      ],
      popular: false,
      badge: "Most popular"
    },
    {
      name: "Bundle",
      price: `$${pricing.bundle_price}`,
      description: "CWMP + Final Report together",
      features: [
        "Everything in CWMP Report",
        "Final Verification Report",
        "Receipt upload & tracking",
        "Variance analysis",
        "Unlimited AI Compliance Memos",
        "Complete compliance package",
        `Save $${bundleSavings}`
      ],
      popular: true,
      badge: "Best Value"
    },
    {
      name: "Subscription",
      price: `$${pricing.subscription_price}`,
      priceNote: "/month",
      description: "Unlimited reports for high-volume users",
      features: [
        "Unlimited CWMP reports",
        "Unlimited Final Reports",
        "Unlimited AI Compliance Memos",
        "All add-on features included",
        "Priority support",
        "Multi-project dashboard",
        "Cancel anytime"
      ],
      popular: false,
      badge: "Unlimited"
    }
  ];

  const steps = [
    { 
      step: 1, 
      title: "Enter Project Details", 
      description: "Address, permit number, building type, and square footage. Takes about 2-3 minutes.",
      tips: ["Have your permit application handy", "Know your total square footage"]
    },
    { 
      step: 2, 
      title: "Review Estimates", 
      description: "Auto-calculated waste estimates based on your building type and CALGreen standards.",
      tips: ["Estimates are based on industry averages", "You can adjust if needed"]
    },
    { 
      step: 3, 
      title: "Download Your Plan", 
      description: "Get your council-ready PDF instantly. Submit with your permit application.",
      tips: ["PDF is ready for building department", "Keep a copy for your records"]
    }
  ];

  // What You'll Need checklist
  const beforeYouStart = [
    { item: "Permit application or number", icon: "📋" },
    { item: "Project address", icon: "📍" },
    { item: "Building type (residential, commercial, etc.)", icon: "🏗️" },
    { item: "Total square footage", icon: "📐" },
    { item: "Owner/contractor contact details", icon: "👤" }
  ];

  // FAQ Data
  const faqs = [
    {
      question: "What is CalGreen 5.408?",
      answer: "CalGreen Section 5.408 requires California construction projects to divert at least 65% of waste from landfills. This applies to most commercial projects and residential projects over 1,000 sq ft (varies by jurisdiction)."
    },
    {
      question: "Which projects need a waste management plan?",
      answer: "New commercial construction, major renovations, demolitions, and residential projects over 1,000 sq ft typically require a CWMP. Requirements vary by county—always verify with your local building department."
    },
    {
      question: "How do I achieve 65% diversion?",
      answer: "Focus on high-diversion materials: concrete (95%+), metals (95%+), asphalt (95%+), and clean wood (70-85%). These typically make up 70-80% of construction waste by weight."
    },
    {
      question: "What documentation do I need?",
      answer: "You'll need a waste management plan, weight tickets from facilities, material quantities, and calculations proving 65%+ diversion. Our plans include all required sections."
    },
    {
      question: "Do requirements vary by county?",
      answer: "Yes. California's 58 counties may have stricter requirements or different thresholds. Our plans include jurisdiction-specific notes for your county."
    },
    {
      question: "What happens if I don't comply?",
      answer: "Non-compliance can result in permit delays, withheld certificates of occupancy, fines, and required corrective action plans."
    }
  ];

  // Material highlights for quick reference
  const materialHighlights = [
    { name: "Concrete & Masonry", rate: "95%+", tip: "Keep separate from wood and trash. Most facilities accept rebar." },
    { name: "Metals", rate: "95-98%", tip: "Separate ferrous from non-ferrous. You typically get paid for scrap." },
    { name: "Asphalt", rate: "95%+", tip: "Don't mix with concrete—different processing and markets." },
    { name: "Clean Wood", rate: "70-85%", tip: "Must be unpainted and untreated. Treated wood is hazardous waste." },
    { name: "Cardboard", rate: "70%", tip: "Keep dry. Most haulers include recycling at no extra cost." },
    { name: "Drywall", rate: "2-10%", tip: "Limited infrastructure in CA. Don't count on it for diversion targets." }
  ];

  // FAQ JSON-LD for the homepage (uses the existing `faqs` data array)
  const homepageJsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "Plan Generator CA",
      url: "https://www.plangeneratorca.com",
      description:
        "California ADU Permit Approval Toolkit — CALGreen waste plans, Title 24 notes, and AI-generated compliance memos.",
      inLanguage: "en-US",
    },
    {
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: "California ADU Permit Documentation",
      name: "ADU Permit Approval Toolkit",
      description:
        "CALGreen Construction Waste Management Plans, Title 24 notes, and AI-generated compliance cover memos for California ADU contractors.",
      provider: { "@type": "Organization", name: "Plan Generator CA" },
      areaServed: { "@type": "State", name: "California" },
      offers: {
        "@type": "AggregateOffer",
        priceCurrency: "USD",
        lowPrice: pricing.cwmp_price,
        highPrice: pricing.subscription_price,
        offerCount: 3,
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.question,
        acceptedAnswer: { "@type": "Answer", text: f.answer },
      })),
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Plan Generator CA | California ADU Permit Approval Toolkit"
        description="Get your California ADU permit approved the first time. CALGreen waste plans, Title 24 notes, and an AI-generated compliance cover memo — every document your plan checker needs, council-ready in under 10 minutes."
        keywords="California ADU, ADU permit, ADU compliance toolkit, CALGreen, CWMP, accessory dwelling unit, Title 24, AI compliance memo, California waste management plan, ADU contractor"
        path="/"
        image="https://www.plangeneratorca.com/og-image.svg"
        jsonLd={homepageJsonLd}
      />
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-6">
              <BrandWordmark size="md" onClick={() => navigate("/")} />
              <CountyNavLinks className="hidden lg:flex" />
            </div>
            <div className="hidden md:flex items-center gap-5">
              <a href="#features" className="text-sm text-foreground/80 hover:text-foreground transition-colors">Features</a>
              <a href="#pricing" className="text-sm text-foreground/80 hover:text-foreground transition-colors">Pricing</a>
              <a href="#faq" className="text-sm text-foreground/80 hover:text-foreground transition-colors">FAQ</a>
              <div className="flex items-center gap-2 ml-2 pl-4 border-l border-border">
                <button
                  onClick={() => navigate('/login')}
                  data-testid="login-btn"
                  className="pill-btn pill-btn-secondary"
                >
                  Sign in
                </button>
                <button
                  onClick={() => navigate('/register')}
                  data-testid="get-started-btn"
                  className="pill-btn pill-btn-primary"
                >
                  Get started
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section — PG-AU inspired layout (text + product cards on left, inline login on right) */}
      <section className="relative pt-28 pb-20 px-4 overflow-hidden">
        <BlueprintBackdrop variant="hero" showIsometric={true} />

        <div className="relative max-w-7xl mx-auto z-10">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-start">

            {/* LEFT — headline + CTAs + 3 product cards underneath */}
            <div className="lg:col-span-8 animate-fade-in-up">
              <div className="pre-headline-pill mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-accent" />
                The online California ADU compliance platform
              </div>
              <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl text-foreground tracking-tight leading-[0.95]">
                ADU Permits.<br />
                <CalGreenWordmark withPeriod /> Title&nbsp;24.<br />
                <span className="headline-accent">Approved on the first submittal.</span>
              </h1>
              <p className="mt-6 text-lg text-muted-foreground max-w-2xl leading-relaxed">
                Generate council-ready CALGreen Construction Waste Management Plans, Final Verification Reports, and AI-powered compliance memos for California ADUs in minutes — covering all 58 counties.
              </p>
              <div className="mt-7 flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => navigate('/register')}
                  data-testid="hero-cta-btn"
                  className="pill-btn pill-btn-primary text-base px-7 py-3.5"
                >
                  Get started
                  <ArrowRight className="w-4 h-4" />
                </button>
                <button
                  onClick={() => document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' })}
                  data-testid="hero-view-plans-btn"
                  className="pill-btn pill-btn-secondary text-base px-7 py-3.5"
                >
                  View plans
                </button>
              </div>

              {/* 3 product-type cards in a row, BELOW the text */}
              <div className="mt-10 grid sm:grid-cols-3 gap-4" data-testid="hero-product-cards">
                <button
                  onClick={() => navigate('/register')}
                  className="surface-card p-5 text-left hover:shadow-lg transition-all"
                  data-testid="product-card-cwmp"
                >
                  <HardHat className="w-7 h-7 text-accent mb-3" />
                  <h3 className="font-display font-bold text-base text-foreground">CALGreen CWMP</h3>
                  <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
                    Pre-construction Construction Waste Management Plan with 65% diversion math &amp; jurisdiction notes.
                  </p>
                  <span className="inline-flex items-center gap-1 text-xs text-accent font-semibold mt-3">
                    Get started <ArrowRight className="w-3 h-3" />
                  </span>
                </button>
                <button
                  onClick={() => navigate('/register')}
                  className="surface-card p-5 text-left hover:shadow-lg transition-all"
                  data-testid="product-card-final"
                >
                  <Building2 className="w-7 h-7 text-accent mb-3" />
                  <h3 className="font-display font-bold text-base text-foreground">Final Verification Report</h3>
                  <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
                    Post-construction verification with receipts, variance analysis &amp; certificate-of-occupancy signoff.
                  </p>
                  <span className="inline-flex items-center gap-1 text-xs text-accent font-semibold mt-3">
                    Get started <ArrowRight className="w-3 h-3" />
                  </span>
                </button>
                <button
                  onClick={() => navigate('/sample-memo')}
                  className="surface-card p-5 text-left hover:shadow-lg transition-all"
                  data-testid="product-card-memo"
                >
                  <Sparkles className="w-7 h-7 text-accent mb-3" />
                  <h3 className="font-display font-bold text-base text-foreground">AI Compliance Memo</h3>
                  <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
                    Plain-English cover letter with CALGreen + Title 24 citations — bundled with every paid plan.
                  </p>
                  <span className="inline-flex items-center gap-1 text-xs text-accent font-semibold mt-3">
                    See sample <ArrowRight className="w-3 h-3" />
                  </span>
                </button>
              </div>
            </div>

            {/* RIGHT — inline login form (PG-AU style) */}
            <aside className="lg:col-span-4 lg:sticky lg:top-24" data-testid="hero-login-card">
              <div className="glass-card p-7">
                <span className="pre-headline-pill text-[0.65rem]">Welcome back</span>
                <h2 className="mt-3 font-display font-black text-2xl text-foreground tracking-tight">
                  Sign in to continue
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">Pick up where you left off.</p>

                <form onSubmit={handleInlineLogin} className="mt-5 space-y-3">
                  <div>
                    <label htmlFor="hero-email" className="block text-[0.65rem] font-bold uppercase tracking-wider text-foreground mb-1.5">
                      Email
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <input
                        id="hero-email"
                        type="email"
                        required
                        value={loginEmail}
                        onChange={(e) => setLoginEmail(e.target.value)}
                        placeholder="you@example.com"
                        className="field-input pl-10"
                        data-testid="hero-email-input"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="hero-password" className="block text-[0.65rem] font-bold uppercase tracking-wider text-foreground mb-1.5">
                      Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <input
                        id="hero-password"
                        type="password"
                        required
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        placeholder="••••••••"
                        className="field-input pl-10"
                        data-testid="hero-password-input"
                      />
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={loginLoading}
                    className="pill-btn pill-btn-primary w-full justify-center mt-2 disabled:opacity-60"
                    data-testid="hero-signin-btn"
                  >
                    {loginLoading ? (
                      <>
                        <svg className="animate-spin w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Signing in…
                      </>
                    ) : (
                      <>
                        Sign in
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </form>

                <p className="mt-5 text-center text-xs text-muted-foreground">
                  Don&apos;t have an account?{" "}
                  <button
                    onClick={() => navigate("/register")}
                    className="text-accent font-semibold hover:underline"
                    data-testid="hero-register-link"
                  >
                    Create one
                  </button>
                </p>
              </div>
            </aside>
          </div>

          {/* Stats bar — PG-AU style */}
          <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 border-t border-border/60 pt-10" data-testid="hero-stats-bar">
            <div>
              <div className="font-display font-black text-3xl lg:text-4xl text-foreground tracking-tight">
                10 <span className="text-accent">min</span>
              </div>
              <div className="text-xs text-muted-foreground mt-1.5 uppercase tracking-wider font-semibold">Avg. time to permit-ready</div>
            </div>
            <div>
              <div className="font-display font-black text-3xl lg:text-4xl text-foreground tracking-tight">
                58
              </div>
              <div className="text-xs text-muted-foreground mt-1.5 uppercase tracking-wider font-semibold">California counties supported</div>
            </div>
            <div>
              <div className="font-display font-black text-3xl lg:text-4xl text-foreground tracking-tight">
                65<span className="text-accent">%</span>+
              </div>
              <div className="text-xs text-muted-foreground mt-1.5 uppercase tracking-wider font-semibold">CALGreen diversion baked in</div>
            </div>
            <div>
              <div className="font-display font-black text-3xl lg:text-4xl text-foreground tracking-tight">
                $1,500<span className="text-accent">+</span>
              </div>
              <div className="text-xs text-muted-foreground mt-1.5 uppercase tracking-wider font-semibold">Saved per avoided rework</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="relative py-24 px-4 border-t border-border/60">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl mb-14">
            <span className="pre-headline-pill mb-4">Why Plan Generator CA</span>
            <h2 className="font-display font-black text-4xl sm:text-5xl text-foreground tracking-tight">
              Built for ADU contractors. <span className="headline-accent">Approved by plan checkers.</span>
            </h2>
            <p className="mt-4 text-base text-muted-foreground max-w-xl">
              Skip the consultant fees and the rejection-rework loop. Permit-ready paperwork in minutes — not weeks.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {features.map((feature, index) => (
              <div
                key={index}
                className="surface-card p-6"
                data-testid={`feature-card-${index}`}
              >
                <div className="w-12 h-12 rounded-xl bg-accent/10 text-accent flex items-center justify-center mb-5">
                  {feature.icon}
                </div>
                <h3 className="font-display font-bold text-xl text-foreground mb-2">{feature.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Compliance Memo Highlight */}
      <section className="relative py-24 px-4 border-t border-border/60">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <div>
            <span className="pre-headline-pill mb-4 inline-flex">
              <span className="w-1.5 h-1.5 rounded-full bg-accent" />
              New · AI Compliance Memo
            </span>
            <h2 className="font-display font-black text-4xl sm:text-5xl text-foreground tracking-tight leading-[0.95]">
              The cover memo your <span className="headline-accent">plan checker actually wants.</span>
            </h2>
            <p className="mt-5 text-base text-muted-foreground leading-relaxed">
              Every paid CWMP now includes an AI-generated, plain-English compliance memo with CALGreen + Title 24 citations and county-specific portal references. Designed to be stamped in 60 seconds.
            </p>
            <ul className="mt-6 space-y-2.5 text-sm text-foreground/90">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                Auto-generated from your project data — zero extra typing
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                Includes code citations (CALGreen §5.408, Title 24 Part 11)
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                Bundle &amp; Subscription get unlimited regenerations
              </li>
            </ul>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => navigate('/sample-memo')}
                className="pill-btn pill-btn-primary"
                data-testid="see-sample-memo-btn"
              >
                See a live sample memo
                <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => navigate('/register')}
                className="pill-btn pill-btn-secondary"
              >
                Create account
              </button>
            </div>
          </div>

          <div className="relative">
            <div className="surface-card overflow-hidden">
              <div className="bg-foreground text-white px-5 py-3 flex items-center justify-between">
                <span className="text-[0.65rem] uppercase tracking-[0.18em] text-white/60 font-bold">Sample memo</span>
                <span className="text-xs text-white/80">CWMP · Los Angeles County</span>
              </div>
              <div className="p-5 sm:p-7 text-sm text-foreground/90 leading-relaxed" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
                <p className="font-semibold text-foreground mb-2">To: Building Department Reviewer, LA County</p>
                <p className="font-semibold text-foreground mb-3">Re: ADU CWMP — 720 sq ft, Magnolia Ave</p>
                <p className="mb-3">
                  We are pleased to submit the enclosed Construction Waste Management Plan in support of the above ADU permit. This submission addresses the requirements of <span className="font-semibold">CALGreen §5.408</span> and Los Angeles County&apos;s waste diversion ordinance…
                </p>
                <p className="mb-3 text-muted-foreground italic">
                  …Concrete (95% diversion), clean lumber (75%), and metals (95%) account for the majority of project tonnage, helping demonstrate the 65% diversion threshold required under <span className="font-semibold not-italic">Title 24 Part 11</span>…
                </p>
                <div className="mt-4 pt-4 border-t border-border">
                  <button
                    onClick={() => navigate('/sample-memo')}
                    className="text-accent text-sm font-semibold hover:underline"
                  >
                    Read the full live sample →
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="relative py-24 px-4 border-t border-border/60 bg-muted/30">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl mb-14">
            <span className="pre-headline-pill mb-4">How it works</span>
            <h2 className="font-display font-black text-4xl sm:text-5xl text-foreground tracking-tight">
              Three steps. <span className="headline-accent">Under 10 minutes.</span>
            </h2>
            <p className="mt-4 text-base text-muted-foreground max-w-xl">
              Get your CALGreen-compliant waste management plan without the consultant ping-pong.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-14">
            {steps.map((item, index) => (
              <div
                key={index}
                className="surface-card p-7 relative animate-fade-in-up"
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`step-${item.step}`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <span className="font-display font-black text-5xl text-accent leading-none">0{item.step}</span>
                  <div className="h-px flex-1 bg-border" />
                </div>
                <h3 className="font-display font-bold text-xl text-foreground mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>

          {/* Before You Start Checklist */}
          <div className="max-w-2xl mx-auto">
            <div className="surface-card p-7">
              <div className="flex items-center gap-2 mb-1">
                <FileText className="w-5 h-5 text-accent" />
                <h3 className="font-display font-bold text-lg text-foreground">Before you start — what you&apos;ll need</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-5">Have these ready to complete your plan quickly.</p>
              <div className="grid sm:grid-cols-2 gap-2.5 mb-6">
                {beforeYouStart.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 p-3 rounded-xl bg-muted/50 border border-border"
                  >
                    <span className="text-lg">{item.icon}</span>
                    <span className="text-sm text-foreground">{item.item}</span>
                  </div>
                ))}
              </div>
              <button
                onClick={() => navigate('/register')}
                className="pill-btn pill-btn-accent"
                data-testid="start-now-btn"
              >
                Start your plan now
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="relative py-24 px-4 border-t border-border/60">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <span className="pre-headline-pill mb-4">Pricing</span>
            <h2 className="font-display font-black text-4xl sm:text-5xl text-foreground tracking-tight">
              Simple. Transparent. <span className="headline-accent">Pay only when you submit.</span>
            </h2>
            <p className="mt-4 text-base text-muted-foreground max-w-xl mx-auto">
              One-off CWMP, Bundle, or unlimited subscription. Pick what fits your project volume.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {plans.map((plan, index) => (
              <div
                key={index}
                className={`relative surface-card p-7 ${plan.popular ? 'ring-2 ring-accent shadow-[0_18px_48px_rgba(61,91,245,0.18)]' : ''}`}
                data-testid={`pricing-card-${plan.name.toLowerCase().replace(' ', '-')}`}
              >
                {plan.badge && (
                  <div className={`absolute -top-3 left-6 ${plan.popular ? 'pre-headline-pill' : 'pre-headline-pill pre-headline-pill-mint'}`}>
                    {plan.badge}
                  </div>
                )}
                <h3 className="font-display font-bold text-2xl text-foreground mt-2">{plan.name}</h3>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className="font-display font-black text-5xl text-foreground tracking-tight">{plan.price}</span>
                  {plan.priceNote && <span className="text-muted-foreground text-sm">{plan.priceNote}</span>}
                </div>
                <p className="mt-2 text-sm text-muted-foreground">{plan.description}</p>

                <ul className="space-y-2.5 mt-6 mb-7">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-foreground/90">
                      <CheckCircle className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <button
                  className={`pill-btn w-full justify-center ${plan.popular ? 'pill-btn-primary' : 'pill-btn-secondary'}`}
                  onClick={() => navigate('/register')}
                  data-testid={`pricing-cta-${plan.name.toLowerCase().replace(' ', '-')}`}
                >
                  Get started
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          {/* Final Report standalone note */}
          <div className="text-center mt-10">
            <p className="text-sm text-muted-foreground">
              Need just a Final Report? <span className="text-accent font-semibold">${pricing.final_report_price}</span> per report
            </p>
          </div>
        </div>
      </section>

      {/* Plan Types */}
      <section className="relative py-24 px-4 border-t border-border/60 bg-muted/30">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl mb-14">
            <span className="pre-headline-pill mb-4">Document types</span>
            <h2 className="font-display font-black text-4xl sm:text-5xl text-foreground tracking-tight">
              Two reports. <span className="headline-accent">Full coverage.</span>
            </h2>
            <p className="mt-4 text-base text-muted-foreground max-w-xl">
              Tailored to California requirements — pre-construction through final verification.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <div className="surface-card overflow-hidden" data-testid="cwmp-card">
              <div className="h-44 bg-foreground flex items-center justify-center relative overflow-hidden">
                <div className="blueprint-grid-dense" aria-hidden="true" />
                <HardHat className="w-16 h-16 text-accent relative z-10" />
              </div>
              <div className="p-6">
                <h3 className="font-display font-bold text-xl text-foreground mb-3">Construction Waste Management Plan</h3>
                <ul className="space-y-2 text-sm text-foreground/80">
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-accent" />Pre-construction submission document</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-accent" />Auto-calculated material estimates</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-accent" />65% diversion compliance</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-accent" />Jurisdiction-specific requirements</li>
                </ul>
              </div>
            </div>

            <div className="surface-card overflow-hidden" data-testid="final-report-card">
              <div className="h-44 bg-accent flex items-center justify-center relative overflow-hidden">
                <div className="blueprint-grid-dense" aria-hidden="true" style={{ opacity: 0.4 }} />
                <Building2 className="w-16 h-16 text-white relative z-10" />
              </div>
              <div className="p-6">
                <h3 className="font-display font-bold text-xl text-foreground mb-3">Final Verification Report</h3>
                <ul className="space-y-2 text-sm text-foreground/80">
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-accent" />Post-construction verification</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-accent" />Actual vs estimated comparison</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-accent" />Receipt &amp; manifest tracking</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-accent" />Compliance certification</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CalGreen 5.408 Overview Section */}
      <section id="calgreen" className="relative py-24 px-4 border-t border-border/60">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="pre-headline-pill mb-4">CALGreen 5.408</span>
            <h2 className="font-display font-black text-4xl sm:text-5xl text-foreground tracking-tight">
              Compliance, <span className="headline-accent">decoded.</span>
            </h2>
            <p className="mt-4 text-base text-muted-foreground max-w-2xl mx-auto">
              California requires 65% waste diversion on construction projects. Here&apos;s what you need to know.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-5 mb-10">
            <div className="surface-card p-6">
              <div className="w-11 h-11 rounded-xl bg-accent/10 flex items-center justify-center mb-4">
                <Scale className="w-5 h-5 text-accent" />
              </div>
              <h3 className="font-display font-bold text-lg text-foreground mb-2">The requirement</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Divert at least <span className="text-foreground font-semibold">65% by weight</span> of construction and demolition debris from landfills through recycling, reuse, or salvage.
              </p>
            </div>

            <div className="surface-card p-6">
              <div className="w-11 h-11 rounded-xl bg-accent/10 flex items-center justify-center mb-4">
                <Building2 className="w-5 h-5 text-accent" />
              </div>
              <h3 className="font-display font-bold text-lg text-foreground mb-2">Who must comply</h3>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Commercial construction &amp; renovations</li>
                <li>• Residential projects over 1,000 sq ft</li>
                <li>• Demolition projects</li>
                <li>• Mixed-use developments</li>
              </ul>
            </div>

            <div className="surface-card p-6">
              <div className="w-11 h-11 rounded-xl bg-[#FFF1C8] flex items-center justify-center mb-4">
                <AlertTriangle className="w-5 h-5 text-[#8B5A00]" />
              </div>
              <h3 className="font-display font-bold text-lg text-foreground mb-2">County variations</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Each of California&apos;s <span className="text-foreground font-semibold">58 counties</span> may have stricter requirements. Some require online portal submissions. Always verify locally.
              </p>
            </div>
          </div>

          <div className="surface-card p-6 max-w-3xl mx-auto">
            <h3 className="font-display font-bold text-lg text-foreground mb-3">Required documentation</h3>
            <div className="grid sm:grid-cols-2 gap-2.5 text-sm">
              <div className="flex items-center gap-2 text-foreground/80">
                <CheckCircle className="w-4 h-4 text-accent flex-shrink-0" />
                Waste management plan
              </div>
              <div className="flex items-center gap-2 text-foreground/80">
                <CheckCircle className="w-4 h-4 text-accent flex-shrink-0" />
                Weight tickets from facilities
              </div>
              <div className="flex items-center gap-2 text-foreground/80">
                <CheckCircle className="w-4 h-4 text-accent flex-shrink-0" />
                Material quantities by type
              </div>
              <div className="flex items-center gap-2 text-foreground/80">
                <CheckCircle className="w-4 h-4 text-accent flex-shrink-0" />
                65%+ diversion calculations
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Materials Quick Guide */}
      <section id="materials" className="relative py-24 px-4 border-t border-border/60 bg-muted/30">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl mb-12">
            <span className="pre-headline-pill mb-4">Material guide</span>
            <h2 className="font-display font-black text-4xl sm:text-5xl text-foreground tracking-tight">
              Diversion rates <span className="headline-accent">by material.</span>
            </h2>
            <p className="mt-4 text-base text-muted-foreground max-w-xl">
              Focus on high-diversion materials to hit your 65% target. Not all materials are equal.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
            {materialHighlights.map((material, index) => {
              const tone = material.rate.includes('95')
                ? 'bg-[#C9F0DC] text-[#115E3B]'
                : material.rate.includes('70')
                ? 'bg-[#FFF1C8] text-[#8B5A00]'
                : 'bg-[#FCD9D6] text-[#8B1F1F]';
              return (
                <div key={index} className="surface-card p-5">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-display font-bold text-foreground">{material.name}</h4>
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${tone}`}>
                      {material.rate}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">{material.tip}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-10 text-center">
            <p className="text-sm text-muted-foreground max-w-2xl mx-auto">
              <span className="text-accent font-semibold">Pro tip:</span> Concrete, metals, asphalt, and clean wood typically represent 70-80% of C&amp;D waste.
              Diverting just these four materials can exceed 65% compliance.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="relative py-24 px-4 border-t border-border/60">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <span className="pre-headline-pill mb-4">FAQ</span>
            <h2 className="font-display font-black text-4xl sm:text-5xl text-foreground tracking-tight">
              Quick answers, <span className="headline-accent">no fluff.</span>
            </h2>
          </div>

          <div className="space-y-2.5">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="surface-card overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-muted/40 transition-colors"
                  data-testid={`faq-${index}`}
                >
                  <span className="font-display font-semibold text-foreground pr-4">{faq.question}</span>
                  <ChevronDown className={`w-5 h-5 text-accent flex-shrink-0 transition-transform ${openFaq === index ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === index && (
                  <div className="px-5 pb-5">
                    <p className="text-sm text-muted-foreground leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* County SEO discovery */}
      <section className="relative py-20 px-4 border-t border-border/60 bg-muted/30">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-2xl mb-10">
            <span className="pre-headline-pill mb-4">Local guides</span>
            <h2 className="font-display font-black text-3xl sm:text-4xl text-foreground tracking-tight">
              Built for your <span className="headline-accent">California county.</span>
            </h2>
            <p className="mt-3 text-base text-muted-foreground max-w-xl">
              Local rules, hauler lists, and portal-specific memo language for California's busiest ADU markets.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            <button
              onClick={() => navigate('/california/los-angeles-adu-permit')}
              className="surface-card p-6 text-left hover:shadow-lg transition-shadow"
              data-testid="county-link-la"
            >
              <div className="text-xs uppercase tracking-wider text-accent font-bold mb-2">Los Angeles</div>
              <h3 className="font-display font-bold text-xl text-foreground">LA County ADU Toolkit</h3>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">LADBS + LA County Public Works portal. LAMC §12.22 streamlining.</p>
              <span className="text-sm text-accent font-semibold mt-3 inline-flex items-center gap-1">
                Open the LA guide <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </button>
            <button
              onClick={() => navigate('/california/san-diego-adu-permit')}
              className="surface-card p-6 text-left hover:shadow-lg transition-shadow"
              data-testid="county-link-sd"
            >
              <div className="text-xs uppercase tracking-wider text-accent font-bold mb-2">San Diego</div>
              <h3 className="font-display font-bold text-xl text-foreground">San Diego ADU Toolkit</h3>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">SDMC §142, DSD submittal, EDCO + Republic recycling routes.</p>
              <span className="text-sm text-accent font-semibold mt-3 inline-flex items-center gap-1">
                Open the SD guide <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </button>
            <button
              onClick={() => navigate('/california/bay-area-adu-permit')}
              className="surface-card p-6 text-left hover:shadow-lg transition-shadow"
              data-testid="county-link-bay"
            >
              <div className="text-xs uppercase tracking-wider text-accent font-bold mb-2">Bay Area</div>
              <h3 className="font-display font-bold text-xl text-foreground">Bay Area ADU Toolkit</h3>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">9 counties: SF, Alameda, Santa Clara, Marin, San Mateo + more.</p>
              <span className="text-sm text-accent font-semibold mt-3 inline-flex items-center gap-1">
                Open the Bay guide <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24 px-4 border-t border-border/60 overflow-hidden">
        <BlueprintBackdrop variant="panel" showIsometric={false} />
        <div className="relative max-w-3xl mx-auto text-center z-10">
          <span className="pre-headline-pill mb-4">Ready when you are</span>
          <h2 className="font-display font-black text-4xl sm:text-5xl text-foreground tracking-tight">
            Ship your next plan <span className="headline-accent">today.</span>
          </h2>
          <p className="mt-4 text-base text-muted-foreground">
            Create your CALGreen-compliant waste management plan in under 10 minutes.
          </p>
          <button
            onClick={() => navigate('/register')}
            data-testid="final-cta-btn"
            className="pill-btn pill-btn-primary text-base px-7 py-3.5 mt-8"
          >
            Create account
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-14 px-4 border-t border-border/60 bg-muted/30">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start gap-10 mb-10">
            <div className="max-w-sm">
              <BrandWordmark size="md" />
              <p className="mt-3 text-sm text-muted-foreground">
                Professional CalGreen-compliant waste management plans for all 58 California counties.
              </p>
              <p className="mt-2 text-xs text-muted-foreground">
                <a href="https://www.plangeneratorca.com" className="hover:text-foreground transition-colors">www.plangeneratorca.com</a>
                <span className="mx-2">•</span>
                <a href="mailto:nate@plangeneratorca.com" className="hover:text-foreground transition-colors">nate@plangeneratorca.com</a>
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-10 text-sm">
              <div>
                <h4 className="font-display font-bold text-foreground mb-3">Product</h4>
                <ul className="space-y-2 text-muted-foreground">
                  <li><a href="#features" className="hover:text-foreground transition-colors">Features</a></li>
                  <li><a href="#pricing" className="hover:text-foreground transition-colors">Pricing</a></li>
                  <li><a href="#how-it-works" className="hover:text-foreground transition-colors">How it works</a></li>
                </ul>
              </div>
              <div>
                <h4 className="font-display font-bold text-foreground mb-3">Local guides</h4>
                <ul className="space-y-2 text-muted-foreground">
                  <li><button onClick={() => navigate('/california/los-angeles-adu-permit')} className="hover:text-foreground transition-colors text-left">Los Angeles ADU</button></li>
                  <li><button onClick={() => navigate('/california/san-diego-adu-permit')} className="hover:text-foreground transition-colors text-left">San Diego ADU</button></li>
                  <li><button onClick={() => navigate('/california/bay-area-adu-permit')} className="hover:text-foreground transition-colors text-left">Bay Area ADU</button></li>
                  <li><button onClick={() => navigate('/sample-memo')} className="hover:text-foreground transition-colors text-left">Sample memo</button></li>
                </ul>
              </div>
              <div>
                <h4 className="font-display font-bold text-foreground mb-3">Resources</h4>
                <ul className="space-y-2 text-muted-foreground">
                  <li><a href="#calgreen" className="hover:text-foreground transition-colors">CalGreen guide</a></li>
                  <li><a href="#materials" className="hover:text-foreground transition-colors">Materials guide</a></li>
                  <li><a href="#faq" className="hover:text-foreground transition-colors">FAQ</a></li>
                </ul>
              </div>
              <div>
                <h4 className="font-display font-bold text-foreground mb-3">Legal</h4>
                <ul className="space-y-2 text-muted-foreground">
                  <li><a href="#disclaimer" className="hover:text-foreground transition-colors">Disclaimer</a></li>
                  <li><a href="#" className="hover:text-foreground transition-colors">Privacy policy</a></li>
                  <li><a href="#" className="hover:text-foreground transition-colors">Terms of service</a></li>
                </ul>
              </div>
            </div>
          </div>

          {/* Legal Disclaimer */}
          <div id="disclaimer" className="border-t border-border pt-6 mb-6">
            <div className="surface-card p-5">
              <h4 className="text-sm font-display font-bold text-foreground mb-2 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-[#8B5A00]" />
                Important disclaimer
              </h4>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Plan Generator is a planning tool, not a compliance certification service. All plans must be reviewed by qualified professionals
                and verified with your local jurisdiction before submission. We are not affiliated with any California state agency, CalRecycle,
                or local building departments. Requirements vary by county—always confirm with your local building department. Information provided
                is for general guidance only and does not constitute legal, engineering, or professional advice. Use of this service does not
                guarantee regulatory compliance or plan acceptance by any jurisdiction.
              </p>
            </div>
          </div>

          {/* Copyright */}
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-muted-foreground border-t border-border pt-6">
            <p>© {new Date().getFullYear()} Plan Generator CA. All rights reserved.</p>
            <p className="text-xs">Serving all 58 California counties</p>
          </div>
        </div>
      </footer>

      {/* Floating Contact Support Button */}
      <div className="fixed bottom-6 right-6 z-40">
        <button
          onClick={() => setShowContactModal(true)}
          className="pill-btn pill-btn-accent shadow-lg"
          data-testid="contact-support-btn"
        >
          <MessageSquare className="w-4 h-4" />
          Support
        </button>
      </div>

      {/* Contact Support Modal */}
      <ContactSupportModal
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
      />
    </div>
  );
};

export default LandingPage;
